'use client';

import { type FC } from 'react';
import { Input, type ResultListItem } from '@/_components';
import { type InputChipsProps } from './InputChips.types';

export const InputChips: FC<InputChipsProps> = ({
  chips,
  chipNames = [],
  chipExtraCount,
  chipsLimit,
  clearable: isClearable,
  clearChipsText,
  isDisabled,
  hasNoChipLimit,
  isLabelHidden,
  isLoading,
  label,
  onChipSelect,
  onChange,
  onClearChips,
  persistClearButton,
  placeholder,
  results,
  setChips,
  value,
  isRequired,
  className,
}) => {
  const chipResults: Array<ResultListItem> = results?.map((item) => {
    const lowerCaseText: string = value.toLowerCase();

    // Enhanced fuzzy matching for names
    let isMatching: boolean = Boolean(
      item?.name?.toLowerCase().includes(lowerCaseText) ||
        item?.value?.toLowerCase().includes(lowerCaseText),
    );

    // If not matching and we have spaces, try word-based matching
    if (!isMatching && lowerCaseText.includes(' ') && item?.name) {
      const searchWords = lowerCaseText
        .split(/\s+/)
        .filter((w) => w.toLowerCase() !== 'and');
      const itemNameLower = item.name.toLowerCase();

      // Check if all words from search appear in the item name
      isMatching = searchWords.every((word) => itemNameLower.includes(word));
    }

    item.isHidden = !isMatching;

    return item;
  });

  const onRemoveChip = (chip: string) => {
    const newChipsSet: Set<string> = new Set(chips);
    newChipsSet.delete(chip);

    onChipSelect?.(newChipsSet);
    setChips(newChipsSet);
  };

  const isAtCapacity: boolean = Boolean(chipsLimit && chipsLimit <= chips.size);

  const onResultSelect = (item: ResultListItem) => {
    const newChipsSet: Set<string> = new Set(chips);
    const hasChip: boolean = chips.has(item?.value!);

    hasChip ? newChipsSet.delete(item?.value!) : newChipsSet.add(item?.value!);

    if (!hasChip && isAtCapacity) return;

    onChipSelect?.(newChipsSet);
    setChips(newChipsSet);
  };

  return (
    <Input
      aria-haspopup="listbox"
      autoComplete="off"
      chips={chips}
      chipExtraCount={chipExtraCount}
      chipNames={chipNames}
      className={className}
      clearable={isClearable}
      clearChipsText={clearChipsText}
      hasNoChipLimit={hasNoChipLimit}
      isRequired={isRequired}
      isLoading={isLoading}
      isDisabled={isAtCapacity || isDisabled}
      isLabelHidden={isLabelHidden}
      label={label}
      onChange={onChange}
      onClearChips={() => {
        onChipSelect?.(new Set());
        setChips(new Set());
        onClearChips?.();
      }}
      onRemoveChip={onRemoveChip}
      onResultSelect={onResultSelect}
      persistClearButton={persistClearButton}
      placeholder={placeholder}
      results={chipResults}
      role="combobox"
      value={value}
      multipleSelect={!chipsLimit}
    />
  );
};
